<template>
    <div>
        <p v-if="getFilteredItemsCount === 0">{{ labels.noItemsMessage }}</p>

        <div class="columnWrapper" :class="{ 'is-grid-mode': (layoutMode == 'grid'), 'is-feed-mode': (layoutMode == 'feed' && galleryVersion == 'modern' )}" ref="columnWrapper" role="list">
            <transition-group name="list" tag="div" role="list">
                <gallery-item v-for="(item) in galleryItems" ref="galleryItemElem" v-show="item.visible" :aria-hidden="item.visible ? 'false' : 'true'" v-bind:key="`item-${item.id}`" :id="`gallery-item-${item.id}`" :item="item" />   
            </transition-group>
        </div>
    </div>
</template>


<script>
    import galleryItem from './galleryItem.vue';
    import { mapState, mapMutations, mapGetters } from 'vuex';
    import { bus } from '../_event-bus';

    import { getImageAspectRatioBasedOnGallery, checkVersion } from '../mixins/helpers';

    export default {
        name: 'GalleryList',
        data() {
            return {
                columns: [],
                columnCount: this.calculateColumnCount(),
                showBorderColors: true,
                loadedViewportSize: null,
                loadedViewportContext: null,
                observer: null,
            }
        },
        created() {
            this.resetColumns();
            bus.$on('ListingItemsChanged', () => {
                this.recalculateHeights()
            });
        },
        mounted() {
            this.$nextTick(() => {
                this.recalculateHeights();

                this.loadedViewportSize = window.innerWidth;
                this.loadedViewportContext = this.calculateViewportContext();
            });

            window.addEventListener('resize', this.resizeWindow);

            this.initaliseImageFadeIntersectionObserver();
            this.setupPreloadImageIntersectionObserver();
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.resizeWindow);
        },

        computed: {
            ...mapGetters('gallery', [
                'getFilteredItemsCount',
                'getFilteredItems',
                'getHiddenItems',
            ]),
    
            ...mapState('gallery', [
                'galleryItems',
                'galleryVersion',
                'loading',
                'layoutMode',
            ]),
            
            ...mapState('translations', [
                'labels',
            ]),
        },

        methods: {
            ...mapMutations('gallery', [
                'updateFilteredItemStyling',
                'updateLoadingStatus',
            ]),

            preloadImageViewportOffset() {
                // This function adds a margin offset to the preload intersection observer to add an amount of space as a buffer for us to load hte image
                if (window.innerWidth < 768) return 300;
                else return 500;
            },
            setupPreloadImageIntersectionObserver() {
                const imgOptions = {
                    threshold: 0,
                    rootMargin: `${this.preloadImageViewportOffset()}px 0px ${this.preloadImageViewportOffset()}px 0px` // These values allow area space for the image to first be lazyloaded, this allows it to start when its "Out of the viewport"
                };

                const imgObserver = new IntersectionObserver((entries, imgObserver) => {
                    entries.forEach(entry => {
                        if (!entry.isIntersecting) {
                            return;
                        }

                        if (entry.target.tagName === 'VIDEO') {
                            this.preloadVideoPauseImage(entry.target);
                        } else {
                            this.preloadImage(entry.target);
                        }

                        imgObserver.unobserve(entry.target);
                    });
                }, imgOptions);

                if (this.$refs.columnWrapper) {
                    this.$refs.columnWrapper.querySelectorAll('.asset-img, .asset-vid').forEach(img => {
                        imgObserver.observe(img);
                    });
                }
            },

            preloadVideoPauseImage(elem) {
                const videoPauseImg = elem.previousElementSibling.querySelector("img.asset-img");
                this.preloadImage(videoPauseImg);    
            },

            preloadImage(elem) {
                // Manually set the image src along with any srcset attributes tell the image to preload
                const srcAttr = elem.getAttribute('data-src-observer');
                if (!srcAttr) return;
                elem.setAttribute('src' ,srcAttr);
                
                const sourceSets = elem.parentElement.querySelectorAll('[data-srcset-observer]');
                sourceSets.forEach(source => {
                    const val = source.getAttribute('data-srcset-observer');
                    if (val) {
                        source.setAttribute('srcset', val);
                        source.removeAttribute('data-srcset-observer');
                    }
                });
                
                elem.onload = function () {
                    elem.removeAttribute('data-src-observer');
                    elem.closest('.gallery-item').classList.add('img-loaded');

                    const customImgLoadedEvent = new CustomEvent('imgNowLoaded');
                    elem.dispatchEvent(customImgLoadedEvent);
                }
            },
            initaliseImageFadeIntersectionObserver() {
                // This function detemines whether to "reveal" and fade in the items in the listing
                if (!window.IntersectionObserver) return;
                if (this.observer) {
                    this.observer.disconnect();
                }

                this.observer = new IntersectionObserver((entries, observer) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            // Using a custom event here so that we can listen to it in vuejs and then store the value as a variable on the Component
                            // rather than just adding a class via vanilla JS
                            const customIsInViewEvent = new CustomEvent('isInView');
                            entry.target.dispatchEvent(customIsInViewEvent);
                        }
                    });
                }, { root: null, rootMargin: '0px', threshold: 0.35 }); 

                if (this.$refs.galleryItemElem && this.$refs.galleryItemElem.length > 0) {
                    this.$refs.galleryItemElem.forEach(component => this.observer.observe(component.$el));
                }
            },

            getFirstRowOfVisibleItems() {
                const colCount = this.calculateColumnCount();
                const items = [];
                let runningCount = 0;

                if (this.$refs.galleryItemElem && this.$refs.galleryItemElem.length > 0) {
                    this.$refs.galleryItemElem.every(component => {
                        // We need to check if the actual item has been filtered or not and whether it is set to be shown using .visible;
                        if (component.item.visible) {
                            items.push(component);
                            runningCount++;
                        }

                        // This returns true/false so we can either skip out the loop or continue
                        return (runningCount < colCount);
                    });

                    return items.length > 0 ? items : null;
                } else return null;  
            },

            showFirstRowOfItems() {
                const firstRow = this.getFirstRowOfVisibleItems();
                if (firstRow == null) return false;

                const customIsInViewEvent = new CustomEvent('isInView');
                firstRow.forEach(component => component.$el.dispatchEvent(customIsInViewEvent));
            },

            toggleBorderColors() { // Only used for debugging, will be removed later
                this.showBorderColors = !this.showBorderColors;
            },
            calculateColumnCount() {
                if (this.galleryVersion === 'legacy') {
                    if (window.innerWidth < 540) return 1;
                    if (window.innerWidth >= 540 && window.innerWidth < 1200) return 2;
                    return 3;
                } else if (this.galleryVersion === 'modern' && this.layoutMode === 'grid') {
                
                    if (window.innerWidth <= 549) return 2;
                    if (window.innerWidth <= 768) return 4;
                    return 4;
                }
                else {
                    return window.innerWidth < 768 ? 1 : 2;
                }
            },

            resetColumns() {
                this.columns = [];
                this.columnCount = this.calculateColumnCount();

                for (let i = 0; i < this.columnCount; i++) {
                    this.columns.push({ id: i, height: 0 });
                }

                if (this.galleryVersion === 'modern') {
                    this.applyModernColumnOffset();
                }

                // Clear any intersection observer functionality tems considered in-view
                if (this.observer) {
                    this.observer.disconnect();
                }

                if (this.$refs.galleryItemElem) {
                    // this.$refs.galleryItemElem.forEach(component => component.$el.classList.remove('is-in-view', 'focused-is-in-view'));
                    this.initaliseImageFadeIntersectionObserver();
                    this.setupPreloadImageIntersectionObserver();
                }
            },

            applyModernColumnOffset() {
                // Start the second column to have a small gap at the top to mimic the design
                if (this.columnCount == 2 && this.layoutMode === 'feed') {
                     // The height value ends up being halfed to become the items marginTop
                    if (window.innerWidth >= 540 && window.innerWidth < 992) this.columns[1].height += 6;
                    else if (window.innerWidth >= 992 && window.innerWidth < 1200) this.columns[1].height += 12;
                    else if (window.innerWidth >= 1200) this.columns[1].height += 16;  
                }
            },

            getImageAspectRatio(ratio) {
                return getImageAspectRatioBasedOnGallery(ratio, this.galleryVersion);
            },

            recalculateHeights() {
                this.resetColumns();

                for (let item of this.getFilteredItems) {
                    const shortestColumnId = this.getShortestColumn();
                    const temp = {}

                    let pixelAspectRatio = item.imageWidth / item.imageHeight;

                    if (this.layoutMode === 'grid') {
                        pixelAspectRatio = 1;
                    }

                    // The containers will use one of three aspect ratios to keep a consistent visual look
                    // They will be square, portrait, and landscape.
                    let heightRelativeToWidth = 100;

                    const toleranceValue = 0.11; // The tolerance value is based upon how close to a square aspect ratio the images aspect ratio should be. Higher number means it will more likely shift to use square image

                    if (pixelAspectRatio < 1 - toleranceValue) {
                        heightRelativeToWidth = this.getImageAspectRatio('portrait');
                        temp.aspect = 'portrait';

                    } else if (pixelAspectRatio > 1 + toleranceValue) {
                        heightRelativeToWidth = this.getImageAspectRatio('landscape');
                        temp.aspect = 'landscape';
                    } else {
                        // Square image
                        temp.aspect = 'square';
                    }

                    item.inlineStyles = {};

                    temp.width = 100 / this.columnCount + '%';
                    temp.left = 100 / this.columnCount * shortestColumnId + '%';
                    temp.aspectRatio = 100 / heightRelativeToWidth;
                    temp.marginTop = this.columns[shortestColumnId].height / this.columnCount + '%';

                    this.updateFilteredItemStyling({
                        id: item.id,
                        width: temp.width,
                        left: temp.left,
                        aspectRatio: temp.aspectRatio,
                        marginTop: temp.marginTop, 
                        aspect: temp.aspect,
                    });

                    this.columns[shortestColumnId].height += heightRelativeToWidth; // Add the items height to the columnSize
                }

                // Running this after so we get the correct minHeight set
                const longestColumnId = this.getLongestColumn();
                if (this.columns[longestColumnId].height === 0) {
                    this.$refs.columnWrapper.style.aspectRatio ='';
                    this.$refs.columnWrapper.style['-webkit-aspect-ratio'] = '';
                } else {
                    if (checkVersion() == null || (checkVersion() && checkVersion().version >= 14.8)) {
                        this.$refs.columnWrapper.style.aspectRatio = 100 / this.columns[longestColumnId].height * this.columnCount;
                        this.$refs.columnWrapper.style['-webkit-aspect-ratio'] = 100 / this.columns[longestColumnId].height * this.columnCount;
                    } else {
                        // Older versions of iOS cannot use aspect ratio so manually calculate the height of the column wrapper
                        const columnWidth = this.$refs.columnWrapper.getBoundingClientRect().width;
                        const aspectRatioPercentage = (100 / this.columns[longestColumnId].height * this.columnCount) * 100;
                        const onePercentOfHeight = columnWidth / aspectRatioPercentage;
                        const totalSize = onePercentOfHeight * 100;
                        this.$refs.columnWrapper.style.height = totalSize + 'px';
                    }
                }

                setTimeout(() => {
                    this.showFirstRowOfItems();
                }, 100);
            },

            getShortestColumn() {
                return this.columns.reduce((shortest, current) => {
                    if (current.height < shortest.height) {
                        return { id: current.id, height: current.height };
                    } else return shortest;
                }, { id: null, height: Infinity }).id;
            },

            getLongestColumn() {
                return this.columns.reduce((longest, current) => {
                    if (current.height > longest.height) {
                        return { id: current.id, height: current.height };
                    } else return longest;
                }, { id: null, height: -1 }).id;
            },

            calculateViewportContext() {
                if (this.galleryVersion === 'legacy') {
                    if (window.innerWidth < 540) return 'mobile';
                    if (window.innerWidth >= 540 && window.innerWidth < 1200) return 'tablet';
                    return 'desktop';

                } else {
                    if (window.innerWidth < 768) return 'mobile';
                    if (window.innerWidth >= 768 && window.innerWidth < 1200) return 'tablet';
                    return 'desktop';
                }              
            },

            resizeWindow() {
                const currentViewport = this.calculateViewportContext(window.innerWidth);

                // If we have swaped between mobile and desktop or vice versa, we want to reset the layout
                if (this.loadedViewportContext != currentViewport) {
                    // If we are going through filter transition on Modern, we want to reload
                    if (this.galleryVersion === 'modern' && 
                        (this.loadedViewportContext === 'mobile' && currentViewport !== 'mobile' ||
                        this.loadedViewportContext !== 'mobile' && currentViewport === 'mobile')) {
                            this.updateLoadingStatus(true); // Show loadinging icon so it hides potentially incorrect filter markup briefly
                            window.location.reload();
                    }

                    this.loadedViewportContext = currentViewport;
                    this.loadedViewportSize = window.innerWidth;
                    this.recalculateHeights();
                }
            },
        },

        watch: {
            loading(newVal, oldVal) {
                if (newVal === false) {
                    this.$nextTick(() => {
                        this.recalculateHeights();
                    });
                }
            },

            layoutMode(newVal, oldVal) {
                this.recalculateHeights();
            }
        },

        components: {
            galleryItem
        },
    }
</script>